import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { X, Pill, Printer } from 'lucide-react';
import { safePrintWindow, escapeHtml } from '../utils/printUtils';
import { VITAL_FIELDS, isVitalAbnormal, isPaedsPatient, vitalChipClass, vitalsTableHtml } from '../utils/vitals';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrescriptionModal = ({ patient, token, onClose }) => {
  const [diagnosis, setDiagnosis] = useState('');
  const [medicines, setMedicines] = useState('');
  const [instructions, setInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedPrescription, setGeneratedPrescription] = useState(null);
  const [manualEntry, setManualEntry] = useState(!patient);
  const [manualName, setManualName] = useState('');
  const [manualAge, setManualAge] = useState('');
  const [manualSex, setManualSex] = useState('');

  const handleGenerate = async () => {
    if (!diagnosis.trim() || !medicines.trim()) {
      toast.error('Please fill diagnosis and medicines');
      return;
    }

    if (manualEntry && (!manualName.trim() || !manualAge || !manualSex)) {
      toast.error('Please fill all patient details');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        diagnosis,
        medicines,
        instructions
      };

      if (manualEntry) {
        payload.patient_name = manualName;
        payload.patient_age = manualAge.toString();
        payload.patient_sex = manualSex;
      } else {
        payload.patient_id = patient.id;
      }

      const response = await axios.post(
        `${API}/prescriptions`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGeneratedPrescription(response.data);
      toast.success('Prescription generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate prescription');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (!generatedPrescription) return;
    const p = generatedPrescription;
    const dateStr = new Date(p.issued_date).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'long', year: 'numeric'
    });

    const kmLogo = `<svg viewBox="0 0 100 100" width="72" height="72" xmlns="http://www.w3.org/2000/svg">
      <path d="M50 6 C 80 18, 86 54, 64 90 C 56 94, 44 94, 36 90 C 14 54, 20 18, 50 6 Z" fill="#0d5c63"/>
      <path d="M50 14 C 74 24, 78 54, 60 82 C 55 84, 45 84, 40 82 C 22 54, 26 24, 50 14 Z" fill="none" stroke="#5eead4" stroke-width="1.2" opacity="0.55"/>
      <text x="50" y="58" text-anchor="middle" fill="#ffffff" font-size="26" font-weight="700" font-family="Georgia, 'Times New Roman', serif">KM</text>
    </svg>`;

    const iUser = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="4"/><path d="M20 21c0-4.4-3.6-8-8-8s-8 3.6-8 8"/></svg>';
    const iCake = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21V10a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v11"/><path d="M4 16h16"/><path d="M12 3v5"/></svg>';
    const iFolder = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 20H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2z"/></svg>';
    const iCal = '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>';
    const iPulse = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>';
    const iPhone = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>';
    const iClock = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';
    const iPin = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>';
    const iCheck = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';

    const stethoBg = `<svg viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%">
      <g fill="none" stroke="#0d5c63" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" opacity="0.06">
        <path d="M120 60 v100 a70 70 0 0 0 140 0 v-100"/>
        <circle cx="120" cy="60" r="10"/>
        <circle cx="260" cy="60" r="10"/>
        <path d="M190 230 v70 a40 40 0 0 0 80 0 v-40"/>
        <circle cx="270" cy="240" r="40"/>
        <circle cx="270" cy="240" r="14" fill="#0d5c63" fill-opacity="0.4"/>
      </g>
    </svg>`;

    const styles = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      @page { size: A4; margin: 6mm; }
      body { font-family: 'Helvetica Neue', Arial, sans-serif; background: white; color: #1f2937; }
      .rx-page { position: relative; padding: 4mm 6mm 2mm 6mm; min-height: 275mm; overflow: hidden; }
      .stetho-watermark { position: absolute; top: 35%; left: 50%; transform: translate(-50%, -50%); width: 380px; height: 380px; pointer-events: none; z-index: 0; }
      .rx-content { position: relative; z-index: 1; }

      /* HEADER */
      .rx-header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 10px; border-bottom: 2.5px solid #0d5c63; }
      .rx-header-left { display: flex; align-items: center; gap: 14px; }
      .rx-header-titles h1 { font-family: Georgia, 'Times New Roman', serif; font-size: 20pt; color: #0d5c63; letter-spacing: 0.5px; font-weight: 700; line-height: 1.05; }
      .rx-header-titles .location { font-size: 10pt; color: #0d5c63; letter-spacing: 5px; margin-top: 3px; font-weight: 600; }
      .rx-header-titles .services { font-size: 8.5pt; color: #64748b; margin-top: 4px; letter-spacing: 0.4px; }
      .rx-tagline { font-family: 'Segoe Script', 'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive; font-size: 20pt; color: #0d5c63; font-style: italic; text-align: right; padding-top: 8px; line-height: 1.1; }

      /* DOCTOR + CLINIC INFO */
      .info-row { display: flex; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #e2e8f0; margin-bottom: 12px; gap: 20px; }
      .info-doctor { flex: 1; }
      .info-doctor .dr-name { font-size: 15pt; font-weight: 700; color: #0f172a; line-height: 1.1; }
      .info-doctor .dr-qual { font-size: 10pt; color: #475569; margin-top: 3px; }
      .info-doctor .dr-title { font-size: 9.5pt; color: #475569; font-style: italic; }
      .info-doctor .dr-reg { font-size: 9pt; color: #64748b; margin-top: 4px; }
      .info-doctor .dr-mottos { display: flex; gap: 14px; margin-top: 8px; font-size: 8.5pt; color: #0d5c63; }
      .info-doctor .dr-mottos span { border-bottom: 1.5px solid #0d5c63; padding-bottom: 2px; font-weight: 600; letter-spacing: 0.3px; }

      .info-clinic { text-align: right; flex: 1; }
      .info-clinic .row { display: flex; align-items: center; justify-content: flex-end; gap: 6px; font-size: 9pt; color: #334155; margin-bottom: 5px; }
      .info-clinic .row svg { color: #0d5c63; flex-shrink: 0; }

      /* PATIENT ROW */
      .patient-row { display: grid; grid-template-columns: 1.4fr 1fr 1.2fr 1fr; gap: 10px; background: #f0fdfa; border-left: 3px solid #0d5c63; padding: 10px 14px; margin-bottom: 12px; }
      .patient-cell { display: flex; align-items: center; gap: 8px; }
      .patient-cell .ico { color: #0d5c63; flex-shrink: 0; display: flex; align-items: center; }
      .patient-cell .label { font-size: 7.5pt; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
      .patient-cell .value { font-size: 10.5pt; font-weight: 600; color: #0f172a; line-height: 1.15; }

      /* DIAGNOSIS */
      .diagnosis-row { display: flex; align-items: center; gap: 10px; padding: 10px 14px; background: #fef2f2; border-left: 3px solid #dc2626; margin-bottom: 12px; }
      .diagnosis-row .lbl { font-size: 9pt; color: #7f1d1d; text-transform: uppercase; letter-spacing: 1.2px; font-weight: 700; }
      .diagnosis-row .dgnval { font-size: 11.5pt; font-weight: 600; color: #0f172a; }
      .diagnosis-row svg { color: #dc2626; }

      /* RX SECTION */
      .rx-section { position: relative; min-height: 220px; padding: 14px 14px 18px 14px; border: 1px dashed #cbd5e1; margin-bottom: 12px; background: rgba(255,255,255,0.5); }
      .rx-symbol { font-family: Georgia, 'Times New Roman', serif; font-size: 64pt; font-weight: 700; color: #0d5c63; opacity: 0.18; position: absolute; top: 4px; left: 14px; line-height: 1; }
      .rx-heading { padding-left: 74px; margin-bottom: 12px; padding-top: 6px; }
      .rx-heading .rx-label { font-size: 10pt; color: #64748b; letter-spacing: 2px; text-transform: uppercase; font-weight: 600; }
      .medicines-list { padding-left: 10px; font-size: 11pt; line-height: 1.85; white-space: pre-line; color: #1f2937; }

      /* ADVICE */
      .advice-section { padding: 10px 14px; background: #fffbeb; border-left: 3px solid #f59e0b; margin-bottom: 12px; }
      .advice-heading { display: flex; align-items: center; gap: 6px; font-size: 9.5pt; color: #92400e; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 6px; }
      .advice-heading svg { color: #f59e0b; }
      .advice-body { font-size: 10pt; color: #1f2937; line-height: 1.65; white-space: pre-line; }

      /* SIGNATURE */
      .sig-block { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 28px; padding: 0 6px; }
      .get-well { font-family: 'Segoe Script', 'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive; font-size: 24pt; color: #0d5c63; font-style: italic; line-height: 1; }
      .sig-right { text-align: right; }
      .sig-line { border-top: 1px solid #0f172a; width: 220px; margin-left: auto; padding-top: 4px; margin-bottom: 4px; }
      .sig-label { font-size: 8.5pt; color: #64748b; letter-spacing: 0.8px; }
      .sig-name { font-size: 11pt; font-weight: 700; color: #0f172a; margin-top: 8px; }
      .sig-qual { font-size: 9pt; color: #475569; margin-top: 1px; }
      .sig-reg { font-size: 8.5pt; color: #64748b; margin-top: 2px; }

      /* FOOTER STRIP */
      .footer-strip { display: flex; justify-content: space-between; align-items: center; margin-top: 18px; padding: 8px 20px; background: #0d5c63; color: white; font-size: 8pt; letter-spacing: 2px; font-weight: 700; text-transform: uppercase; }
      .footer-strip span.item { flex: 1; text-align: center; }
      .footer-strip .sep { color: rgba(255,255,255,0.35); }

      /* VITALS ROW */
      .vitals-row { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
      .vitals-row span { font-size: 9pt; font-weight: 700; color: #0f766e; background: #f0fdfa; border: 1px solid #99f6e4; padding: 3px 12px; border-radius: 999px; }
    `;

    const vitalsRowHtml = vitalsTableHtml(p.vitals || {}, isPaedsPatient(p), 10, '0 0 12px');

    const body = `<div class="rx-page">
      <div class="stetho-watermark">${stethoBg}</div>
      <div class="rx-content">

        <div class="rx-header">
          <div class="rx-header-left">
            ${kmLogo}
            <div class="rx-header-titles">
              <h1>K.M. MULTI-SPECIALITY CLINIC</h1>
              <div class="location">KOLIYANUR</div>
              <div class="services">General Medicine &nbsp;|&nbsp; Family Health &nbsp;|&nbsp; Preventive Care</div>
            </div>
          </div>
          <div class="rx-tagline">Better Health<br/>Brighter Lives</div>
        </div>

        <div class="info-row">
          <div class="info-doctor">
            <div class="dr-name">Dr. K. Mouhamed Nazar</div>
            <div class="dr-qual">MD (General Medicine)</div>
            <div class="dr-title">Consultant Physician</div>
            <div class="dr-reg">Reg. No.: 110764</div>
            <div class="dr-mottos"><span>Evidence Based Care</span><span>Personalised Attention</span></div>
          </div>
          <div class="info-clinic">
            <div class="row">${iPin}<span>K.M. Multi-Speciality Clinic, Koliyanur, Villupuram - 605103</span></div>
            <div class="row">${iPhone}<span>+91 86085 95239</span></div>
            <div class="row">${iClock}<span>Mon - Sat : 6.00 PM - 10.30 PM (Sunday Holiday)</span></div>
          </div>
        </div>

        <div class="patient-row">
          <div class="patient-cell"><span class="ico">${iUser}</span><div><div class="label">Patient Name</div><div class="value">${escapeHtml(p.patient_name)}</div></div></div>
          <div class="patient-cell"><span class="ico">${iCake}</span><div><div class="label">Age / Sex</div><div class="value">${escapeHtml(p.patient_age)} / ${escapeHtml(p.patient_sex)}</div></div></div>
          <div class="patient-cell"><span class="ico">${iFolder}</span><div><div class="label">UHID / Token</div><div class="value">${escapeHtml(p.token_number || '—')}</div></div></div>
          <div class="patient-cell"><span class="ico">${iCal}</span><div><div class="label">Date</div><div class="value">${escapeHtml(dateStr)}</div></div></div>
        </div>

        ${vitalsRowHtml}

        <div class="diagnosis-row">
          ${iPulse}
          <span class="lbl">Diagnosis</span>
          <span class="dgnval">${escapeHtml(p.diagnosis)}</span>
        </div>

        <div class="rx-section">
          <div class="rx-symbol">&#8478;</div>
          <div class="rx-heading">
            <span class="rx-label">Prescription</span>
          </div>
          <div class="medicines-list">${escapeHtml(p.medicines)}</div>
        </div>

        ${p.instructions ? `<div class="advice-section">
          <div class="advice-heading">${iCheck}<span>Advice</span></div>
          <div class="advice-body">${escapeHtml(p.instructions)}</div>
        </div>` : ''}

        <div class="sig-block">
          <div class="get-well">Get Well Soon!</div>
          <div class="sig-right">
            <div class="sig-line"></div>
            <div class="sig-label">Signature: ____________________</div>
            <div class="sig-name">Dr. K. Mouhamed Nazar</div>
            <div class="sig-qual">MD (General Medicine)</div>
            <div class="sig-reg">Reg. No.: 110764</div>
          </div>
        </div>

        <div class="footer-strip">
          <span class="item">Personalised Care</span>
          <span class="sep">•</span>
          <span class="item">Preventive Health</span>
          <span class="sep">•</span>
          <span class="item">Trusted by Families</span>
          <span class="sep">•</span>
          <span class="item">Healthier Tomorrows</span>
        </div>

      </div>
    </div>`;

    safePrintWindow('Prescription - K.M. Multi-Speciality Clinic', `<style>${styles}</style>${body}`);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 no-print" onClick={onClose}>
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto print-content-wrapper" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 print:p-8">
          <div className="flex items-center justify-between mb-6 print:hidden">
            <div className="flex items-center gap-2">
              <Pill className="h-6 w-6 text-primary" />
              <h2 className="text-2xl font-bold">Prescription</h2>
            </div>
            <Button onClick={onClose} variant="ghost" size="icon">
              <X className="h-5 w-5" />
            </Button>
          </div>

          {!generatedPrescription ? (
            <div className="space-y-4">
              {patient ? (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Patient: {patient.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Token: {patient.token_number} • Age: {patient.age} • Sex: {patient.sex}
                      </p>
                    </div>
                    <Button
                      onClick={() => setManualEntry(!manualEntry)}
                      variant="outline"
                      size="sm"
                    >
                      {manualEntry ? 'Use Token Patient' : 'Manual Entry'}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Manual patient entry mode</p>
              )}

              {manualEntry && (
                <div className="space-y-3 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm font-semibold">Patient Details (Manual Entry)</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      placeholder="Patient Name"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      data-testid="prescription-manual-name-input"
                    />
                    <Input
                      type="number"
                      placeholder="Age"
                      value={manualAge}
                      onChange={(e) => setManualAge(e.target.value)}
                      data-testid="prescription-manual-age-input"
                    />
                    <select
                      value={manualSex}
                      onChange={(e) => setManualSex(e.target.value)}
                      className="h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm"
                      data-testid="prescription-manual-sex-select"
                    >
                      <option value="">Sex</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="diagnosis">Diagnosis</Label>
                <Input
                  id="diagnosis"
                  value={diagnosis}
                  onChange={(e) => setDiagnosis(e.target.value)}
                  placeholder="Enter diagnosis"
                  data-testid="prescription-diagnosis-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="medicines">Medicines</Label>
                <textarea
                  id="medicines"
                  value={medicines}
                  onChange={(e) => setMedicines(e.target.value)}
                  placeholder="Enter medicines (one per line)&#10;Example:&#10;1. Tab. Paracetamol 500mg - 1-0-1 x 3 days&#10;2. Tab. Amoxicillin 250mg - 1-1-1 x 5 days"
                  className="w-full min-h-[120px] p-3 border border-input rounded-md font-mono text-sm"
                  data-testid="medicines-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="instructions">Instructions (Optional)</Label>
                <textarea
                  id="instructions"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Additional instructions (e.g., Take after meals, Avoid cold drinks)"
                  className="w-full min-h-[80px] p-3 border border-input rounded-md"
                  data-testid="instructions-input"
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleGenerate} disabled={loading} className="flex-1" data-testid="generate-prescription-button">
                  {loading ? 'Generating...' : 'Generate Prescription'}
                </Button>
                <Button onClick={onClose} variant="outline">Cancel</Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="print:hidden mb-4 flex gap-2">
                <Button onClick={handlePrint} className="flex-1">
                  <Printer className="h-4 w-4 mr-2" />
                  Print Prescription
                </Button>
                <Button onClick={onClose} variant="outline">Close</Button>
              </div>

              {/* Prescription Preview - Matches Print Design */}
              <div id="prescription-print-content" className="rounded-lg bg-white overflow-hidden shadow-sm" style={{ borderTop: '3px solid #0d5c63' }}>
                <div className="p-4 sm:p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between pb-3 border-b-2" style={{ borderColor: '#0d5c63' }}>
                    <div className="flex items-center gap-3">
                      <svg viewBox="0 0 100 100" width="56" height="56" xmlns="http://www.w3.org/2000/svg">
                        <path d="M50 6 C 80 18, 86 54, 64 90 C 56 94, 44 94, 36 90 C 14 54, 20 18, 50 6 Z" fill="#0d5c63"/>
                        <text x="50" y="60" textAnchor="middle" fill="#ffffff" fontSize="26" fontWeight="700" fontFamily="Georgia, serif">KM</text>
                      </svg>
                      <div>
                        <div className="font-bold leading-tight" style={{ color: '#0d5c63', fontFamily: 'Georgia, serif', fontSize: '15px', letterSpacing: '0.3px' }}>K.M. MULTI-SPECIALITY CLINIC</div>
                        <div className="font-semibold" style={{ color: '#0d5c63', fontSize: '9px', letterSpacing: '4px' }}>KOLIYANUR</div>
                        <div className="text-slate-500 mt-1" style={{ fontSize: '8px' }}>General Medicine  |  Family Health  |  Preventive Care</div>
                      </div>
                    </div>
                    <div className="italic text-right leading-tight" style={{ color: '#0d5c63', fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontSize: '18px' }}>
                      Better Health<br/>Brighter Lives
                    </div>
                  </div>

                  {/* Doctor + Clinic */}
                  <div className="flex justify-between gap-4 py-3 border-b border-slate-200">
                    <div className="flex-1">
                      <div className="font-bold text-slate-900" style={{ fontSize: '13px' }}>Dr. K. Mouhamed Nazar</div>
                      <div className="text-slate-600 text-xs">MD (General Medicine)</div>
                      <div className="text-slate-600 text-xs italic">Consultant Physician</div>
                      <div className="text-slate-500 text-[10px] mt-1">Reg. No.: 110764</div>
                      <div className="flex gap-3 mt-2" style={{ color: '#0d5c63' }}>
                        <span className="border-b-2 pb-0.5 text-[9px] font-semibold" style={{ borderColor: '#0d5c63' }}>Evidence Based Care</span>
                        <span className="border-b-2 pb-0.5 text-[9px] font-semibold" style={{ borderColor: '#0d5c63' }}>Personalised Attention</span>
                      </div>
                    </div>
                    <div className="text-right flex-1 space-y-1 text-slate-700" style={{ fontSize: '10px' }}>
                      <div>📍 Koliyanur, Villupuram - 605103</div>
                      <div>📞 +91 86085 95239</div>
                      <div>🕕 Mon - Sat : 6.00 PM - 10.30 PM</div>
                    </div>
                  </div>

                  {/* Patient row */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 p-3 rounded" style={{ background: '#f0fdfa', borderLeft: '3px solid #0d5c63' }}>
                    <div>
                      <div className="text-[8px] uppercase text-slate-500 tracking-wider">Patient</div>
                      <div className="font-semibold text-slate-900 text-sm">{generatedPrescription.patient_name}</div>
                    </div>
                    <div>
                      <div className="text-[8px] uppercase text-slate-500 tracking-wider">Age / Sex</div>
                      <div className="font-semibold text-slate-900 text-sm">{generatedPrescription.patient_age} / {generatedPrescription.patient_sex}</div>
                    </div>
                    <div>
                      <div className="text-[8px] uppercase text-slate-500 tracking-wider">UHID / Token</div>
                      <div className="font-semibold text-slate-900 text-sm">{generatedPrescription.token_number || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[8px] uppercase text-slate-500 tracking-wider">Date</div>
                      <div className="font-semibold text-slate-900 text-sm">
                        {new Date(generatedPrescription.issued_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                  </div>

                  {/* Vitals chips */}
                  {generatedPrescription.vitals && VITAL_FIELDS.some(f => generatedPrescription.vitals[f.key]) && (
                    <div className="flex items-center gap-1.5 flex-wrap mt-3" data-testid="rx-preview-vitals">
                      {VITAL_FIELDS.filter(f => generatedPrescription.vitals[f.key]).map(f => (
                        <span key={f.key} className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${vitalChipClass(isVitalAbnormal(f.key, generatedPrescription.vitals[f.key], isPaedsPatient(generatedPrescription)))}`}>{f.label} {generatedPrescription.vitals[f.key]} {f.unit}</span>
                      ))}
                    </div>
                  )}

                  {/* Diagnosis */}
                  <div className="mt-3 p-3 rounded flex items-center gap-3" style={{ background: '#fef2f2', borderLeft: '3px solid #dc2626' }}>
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#7f1d1d' }}>Diagnosis</span>
                    <span className="font-semibold text-slate-900 text-sm">{generatedPrescription.diagnosis}</span>
                  </div>

                  {/* Rx */}
                  <div className="relative mt-3 p-4 border border-dashed border-slate-300 rounded">
                    <div className="absolute left-3 top-0 font-bold leading-none pointer-events-none" style={{ color: '#0d5c63', opacity: 0.18, fontSize: '54pt', fontFamily: 'Georgia, serif' }}>℞</div>
                    <div className="pl-16">
                      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Prescription</div>
                    </div>
                    <div className="whitespace-pre-line text-sm text-slate-800 leading-relaxed pl-2">
                      {generatedPrescription.medicines}
                    </div>
                  </div>

                  {generatedPrescription.instructions && (
                    <div className="mt-3 p-3 rounded" style={{ background: '#fffbeb', borderLeft: '3px solid #f59e0b' }}>
                      <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: '#92400e' }}>◆ Advice</div>
                      <div className="text-sm text-slate-800 whitespace-pre-line leading-relaxed">{generatedPrescription.instructions}</div>
                    </div>
                  )}

                  {/* Signature */}
                  <div className="flex justify-between items-end mt-8 pt-2">
                    <div className="italic" style={{ color: '#0d5c63', fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontSize: '22px' }}>Get Well Soon!</div>
                    <div className="text-right">
                      <div className="border-t border-slate-900 w-40 ml-auto pt-1" />
                      <div className="text-[9px] text-slate-500">Signature: ____________</div>
                      <div className="font-bold text-slate-900 mt-2 text-sm">Dr. K. Mouhamed Nazar</div>
                      <div className="text-[10px] text-slate-600">MD (General Medicine)</div>
                      <div className="text-[9px] text-slate-500">Reg. No.: 110764</div>
                    </div>
                  </div>
                </div>

                {/* Footer strip */}
                <div className="flex items-center justify-between px-4 py-2 text-white text-[9px] font-bold uppercase tracking-widest" style={{ background: '#0d5c63' }}>
                  <span className="flex-1 text-center">Personalised Care</span>
                  <span className="opacity-40">•</span>
                  <span className="flex-1 text-center">Preventive Health</span>
                  <span className="opacity-40">•</span>
                  <span className="flex-1 text-center">Trusted by Families</span>
                  <span className="opacity-40">•</span>
                  <span className="flex-1 text-center">Healthier Tomorrows</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default PrescriptionModal;
